import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { noop } from '@lux/helpers';

import ErrorModal from '../ErrorModal';
import notifications from '../../config/notifications';
import { ChooseSIMType } from '../ChooseSIMType';
import {
  Selector,
  Spacing,
  Row,
  Column,
  Text,
  TextLink,
  StatusAlert
} from '@dls/web';
import colours from '../../theme/colours';
import { SkeletonGrid } from '../SkeletonLoader';
import { trans as t } from '../../helpers/localisation';

const NumberWrapper = styled.div`
  margin-right: -8px;
  margin-left: -8px;
`;

const NumberSelectNew = ({
  errorCount,
  errorStatus,
  isNgnisError,
  numbers,
  onClick,
  onModalClose,
  onRefresh,
  chooseSIMOptions,
  selectedProduct,
  isLoading,
  skipAddons
}) => {
  const [selectedNumber, setSelectedNumber] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const errorMessage = errorStatus && notifications[errorStatus];

  /**
   *
   * @param {String} number - The number the user has selected
   */
  const handleNumberClick = number => {
    setSelectedNumber(number);
  };

  const handleAddOnsClick = selectedSIMType => {
    onClick(selectedNumber, '', selectedSIMType);
  };

  /**
   * Handles the button click in the modal
   *
   * When reserved number clicked exceeds 3 times, the modal button will trigger the function to refresh the page
   */
  const handleModalButtonClick = () => {
    if (errorCount >= 3) {
      onRefresh();
      setSelectedNumber('');
    }

    setModalOpen(false);
    onModalClose();
  };

  useEffect(() => {
    if (errorStatus) {
      setModalOpen(true);
    }
  }, [errorStatus]);

  const renderNGNISResult = () => {
    const ngnisFragment = (
      <NumberWrapper>
        <Row>
          {numbers &&
            numbers.length > 0 &&
            numbers.map((number, index) => (
              <Column key={number} xs={6} sm={3}>
                <Selector
                  value={number}
                  selected={selectedNumber === number}
                  onClick={v => handleNumberClick(v)}
                >
                  <Selector.Body title={number} css={{ textAlign: 'center' }} />
                </Selector>
              </Column>
            ))}
        </Row>
        {selectedNumber && (
          <ChooseSIMType
            transactionTypeValue="newSignup"
            scrollToSimType
            data={chooseSIMOptions}
            onSimTypeOnSelection={handleAddOnsClick}
            selectedProduct={selectedProduct}
            btnText={skipAddons ? t('PROCEED') : t('CHOOSE_ADD_ONS')}
          />
        )}
      </NumberWrapper>
    );

    return ngnisFragment;
  };

  if (isLoading) {
    return <SkeletonGrid numberOfColumn={4} />;
  }

  return (
    <Fragment>
      {isNgnisError ? (
        <Row>
          <Column sm={12} md={8} noGutter>
            <Spacing bottom={3}>
              <StatusAlert
                title={notifications.NGNIS_SEARCH_NUMBERS_ERROR.text}
                type="alert"
              />
            </Spacing>
            <Text type="smallBody" color={colours.grey_600}>
              Please{' '}
              <TextLink inline onClick={onRefresh}>
                try again
              </TextLink>{' '}
              by refreshing this page. If the error still persists, please visit
              our retail store or call 1609.
            </Text>
          </Column>
        </Row>
      ) : (
        renderNGNISResult()
      )}
    </Fragment>
  );
};

NumberSelectNew.defaultProps = {
  onClick: noop,
  onModalClose: noop,
  onRefresh: noop
};

NumberSelectNew.propTypes = {
  /**
   * Number of error a reserved number is triggered
   */
  errorCount: PropTypes.number,
  /**
   * The error status
   */
  errorStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * Check if the error is a NGNIS error
   */
  isNgnisError: PropTypes.bool,
  /**
   * Load the numbers to be displayed
   */
  numbers: PropTypes.arrayOf(PropTypes.string),
  /**
   * Fire the click event
   */
  onClick: PropTypes.func,
  /**
   * Fire the modal close event
   */
  onModalClose: PropTypes.func,
  /**
   * Handles the refresh event when clicked
   */
  onRefresh: PropTypes.func,
  isLoading: PropTypes.bool
};

export default NumberSelectNew;
